import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { DropCallback } from '../../../stores/types/user-interface';
import { DRAG_TYPE } from '../../../stores/reducers/user-interface';
import { ROOM_STATES } from '../../../stores/reducers/settings';
import useDropArea from '../../../hooks/use-dnd/use-drop-area';
import { GreenroomState as GS } from '../../../stores/types/types';
import { useLivekit } from '../../../contexts/livekit/livekit-context';
import useShotMemos from '../../../hooks/use-shot-memos';
import DefaultSafetyImage from '../../../images/default-safety-image.png';

interface Props {
	feedPosition: number;
	ingressStreamId: string;
	dropCallback: DropCallback;
	isSpotlightFeed?: boolean;
}

export default function FeedIngressStream(props: Props) {
	const { feedPosition, ingressStreamId, dropCallback, isSpotlightFeed = false } = props;

	const ingressSessions = useSelector((s: GS) => s.LivekitReducer.livekitIngresses);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);

	const session = ingressSessions.find((s) => s.id === ingressStreamId);
	const { isOnCanvas } = useShotMemos();

	const adjustedVolume = useMemo(() => {
		if (!session?.volume) return 0;
		if (isOnCanvas || embed || roomState === ROOM_STATES.EDITING) return session.volume;
		return session.volume * backstageCanvasVolume;
	}, [session?.volume, isOnCanvas, embed, roomState, backstageCanvasVolume]);

	const { participantsMap } = useLivekit();
	const participant = session ? (participantsMap.get(session.participantIdentity) ?? null) : null;
	const videoTrack = participant?.videoTrack ?? null;
	const audioTrack = participant?.audioTrack ?? null;

	const videoRef = useRef<HTMLVideoElement>(null);
	const audioRef = useRef<HTMLAudioElement>(null);

	const { isOver, dropRef } = useDropArea({
		accepts: DRAG_TYPE.FEEDS,
		dropCallback,
		dropAreaData: { feedPosition },
	});

	useEffect(() => {
		const element = videoRef.current;
		if (!element || !videoTrack) return;
		videoTrack.attach(element);
		return () => { videoTrack.detach(element); };
	}, [videoTrack]);

	useEffect(() => {
		const element = audioRef.current;
		if (!element || !audioTrack) return;
		audioTrack.attach(element);
		return () => { audioTrack.detach(element); };
	}, [audioTrack]);

	useEffect(() => {
		if (!audioRef.current) return;
		audioRef.current.volume = session?.muted ? 0 : adjustedVolume;
	}, [session?.muted, adjustedVolume, audioTrack]);

	const showVideo = videoTrack && !session?.videoMuted;

	return (
		<>
			{isOver && <div className="dnd-highlighted" />}
			<div className="media" ref={dropRef}>
				<div className="flex-center height-full width-full">
					{showVideo ? (
						<video
							ref={videoRef}
							autoPlay
							playsInline
							muted
							className="media-video"
						/>
					) : (
						<img
							className="media-img"
							src={DefaultSafetyImage}
							alt="Safety Image"
						/>
					)}
					{audioTrack && <audio ref={audioRef} autoPlay />}
				</div>
			</div>
		</>
	);
}
